import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { BookOpen, Lock, Zap, Phone, Building2, Globe, Search, AlertTriangle, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import PricingDialog from "@/components/PricingDialog";

interface CountryGuide {
  country: string;
  emergencyNumber: string;
  policeNumber: string;
  ambulanceNumber: string;
  fireNumber: string;
  travelAdvisory: "safe" | "caution" | "avoid" | "do_not_travel";
  advisoryText: string;
  embassies: { country: string; phone: string; address: string }[];
  tips: string[];
}

const GUIDES: Record<string, CountryGuide> = {
  ukraine: {
    country: "Ukraine",
    emergencyNumber: "112",
    policeNumber: "102",
    ambulanceNumber: "103",
    fireNumber: "101",
    travelAdvisory: "do_not_travel",
    advisoryText: "Active military conflict. Critical risk to civilian safety. Most foreign embassies have evacuated from Kyiv.",
    embassies: [
      { country: "🇺🇸 US", phone: "+380-44-521-5000", address: "4 A.I. Sikorsky St, Kyiv" },
      { country: "🇬🇧 UK", phone: "+380-44-490-3660", address: "9 Desiatynna St, Kyiv" },
      { country: "🇩🇪 Germany", phone: "+380-44-281-7888", address: "25 B.Khmelnytskogo St, Kyiv" },
    ],
    tips: [
      "Download Ukraine Air Raid Alert app (Повітряна тривога)",
      "Know your nearest bomb shelter — marked with blue signs",
      "Keep documents and emergency cash accessible at all times",
      "Register with your embassy for crisis updates",
      "Avoid areas near military infrastructure",
    ],
  },
  israel: {
    country: "Israel",
    emergencyNumber: "112",
    policeNumber: "100",
    ambulanceNumber: "101",
    fireNumber: "102",
    travelAdvisory: "avoid",
    advisoryText: "Active conflict in Gaza border areas. Rocket attacks possible across southern and central Israel. Iron Dome provides partial protection.",
    embassies: [
      { country: "🇺🇸 US", phone: "+972-3-519-7475", address: "71 HaYarkon St, Tel Aviv" },
      { country: "🇬🇧 UK", phone: "+972-3-725-1222", address: "192 HaYarkon St, Tel Aviv" },
      { country: "🇩🇪 Germany", phone: "+972-3-693-1313", address: "3 Daniel Frisch St, Tel Aviv" },
    ],
    tips: [
      "Download Home Front Command app (פיקוד העורף)",
      "Know your nearest shelter — you have 15-90 seconds depending on location",
      "Gaza border: 15 seconds | Ashkelon: 30s | Tel Aviv: 90s",
      "When siren sounds: move to shelter, sit on floor, cover head",
      "Keep phone charged for Red Alert notifications",
    ],
  },
  iraq: {
    country: "Iraq",
    emergencyNumber: "112",
    policeNumber: "104",
    ambulanceNumber: "122",
    fireNumber: "115",
    travelAdvisory: "do_not_travel",
    advisoryText: "High risk of terrorism, kidnapping, and armed conflict. Avoid all travel outside Kurdistan Region.",
    embassies: [
      { country: "🇺🇸 US", phone: "+964-760-030-3000", address: "Al-Kindi St, Baghdad" },
      { country: "🇬🇧 UK", phone: "+964-790-192-6280", address: "International Zone, Baghdad" },
    ],
    tips: [
      "Avoid travel outside major cities, especially at night",
      "Keep copies of all travel documents separately",
      "Register with your embassy before travel",
      "Avoid photographing military installations or checkpoints",
      "Carry ID at all times — checkpoints are frequent",
    ],
  },
  lebanon: {
    country: "Lebanon",
    emergencyNumber: "112",
    policeNumber: "112",
    ambulanceNumber: "140",
    fireNumber: "175",
    travelAdvisory: "avoid",
    advisoryText: "Risk of armed conflict, especially near southern border. Economic crisis has degraded infrastructure and services.",
    embassies: [
      { country: "🇺🇸 US", phone: "+961-4-542-600", address: "Awkar, Beirut" },
      { country: "🇬🇧 UK", phone: "+961-1-960-800", address: "Serail Hill, Beirut" },
      { country: "🇫🇷 France", phone: "+961-1-420-000", address: "Rue de Damas, Beirut" },
    ],
    tips: [
      "Avoid southern Lebanon near Israeli border",
      "Power outages are frequent — carry powerbanks",
      "Keep USD cash as local currency is unstable",
      "Avoid political demonstrations and protests",
      "Monitor local news for sudden escalations",
    ],
  },
  syria: {
    country: "Syria",
    emergencyNumber: "112",
    policeNumber: "112",
    ambulanceNumber: "110",
    fireNumber: "113",
    travelAdvisory: "do_not_travel",
    advisoryText: "Active civil war, terrorism, and kidnapping. No consular assistance available from most Western countries.",
    embassies: [],
    tips: [
      "Most Western embassies are closed — no consular support",
      "Do not travel to Syria under any circumstances",
      "If already present, leave immediately via safest route",
      "Border crossings may close without warning",
      "Communication networks unreliable across most regions",
    ],
  },
  sudan: {
    country: "Sudan",
    emergencyNumber: "999",
    policeNumber: "999",
    ambulanceNumber: "333",
    fireNumber: "998",
    travelAdvisory: "do_not_travel",
    advisoryText: "Armed conflict between SAF and RSF. Severe humanitarian crisis. Khartoum and Darfur most affected.",
    embassies: [],
    tips: [
      "Most foreign embassies have suspended operations",
      "Fighting can erupt suddenly in any area",
      "Water and food supplies are severely limited",
      "Medical facilities are largely non-functional",
      "Avoid Khartoum, Darfur, and border regions",
    ],
  },
  afghanistan: {
    country: "Afghanistan",
    emergencyNumber: "112",
    policeNumber: "119",
    ambulanceNumber: "112",
    fireNumber: "118",
    travelAdvisory: "do_not_travel",
    advisoryText: "Taliban government in control. Extreme risk of terrorism, kidnapping, and detention. No consular assistance from most countries.",
    embassies: [],
    tips: [
      "No Western embassy presence — zero consular support",
      "Women face extreme restrictions on movement and dress",
      "Avoid all gatherings and demonstrations",
      "IED and mine risk remains high across rural areas",
      "Do not photograph Taliban members or checkpoints",
    ],
  },
  iran: {
    country: "Iran",
    emergencyNumber: "112",
    policeNumber: "110",
    ambulanceNumber: "115",
    fireNumber: "125",
    travelAdvisory: "avoid",
    advisoryText: "Risk of detention of foreign nationals. Regional tensions with Israel and Gulf states. Protests may erupt with little warning.",
    embassies: [
      { country: "🇬🇧 UK", phone: "+98-21-6670-5011", address: "Ferdowsi Ave, Tehran" },
      { country: "🇩🇪 Germany", phone: "+98-21-3999-0000", address: "Ferdowsi Ave, Tehran" },
      { country: "🇫🇷 France", phone: "+98-21-6493-3500", address: "Nofel Loshato St, Tehran" },
    ],
    tips: [
      "US citizens: No US embassy — contact Swiss Embassy for consular protection",
      "VPN required for most Western websites and social media",
      "Women must wear hijab in public — strict dress code enforced",
      "Do not photograph government buildings, military, or police",
      "Dual nationals face higher risk of arbitrary detention",
    ],
  },
  qatar: {
    country: "Qatar",
    emergencyNumber: "999",
    policeNumber: "999",
    ambulanceNumber: "999",
    fireNumber: "999",
    travelAdvisory: "caution",
    advisoryText: "Generally safe but regional tensions persist. Strict local laws apply. Proximity to Gulf conflict zones requires awareness.",
    embassies: [
      { country: "🇺🇸 US", phone: "+974-4496-6000", address: "Al Luqta District, Doha" },
      { country: "🇬🇧 UK", phone: "+974-4496-2000", address: "Al Jazeera Tower, Doha" },
    ],
    tips: [
      "Respect local customs — public displays of affection are illegal",
      "Alcohol only in licensed hotels and restaurants",
      "Dress modestly in public — shoulders and knees covered",
      "Do not photograph people without permission",
      "Stay aware of regional missile threats from Iran/Yemen",
    ],
  },
  uae: {
    country: "United Arab Emirates",
    emergencyNumber: "999",
    policeNumber: "999",
    ambulanceNumber: "998",
    fireNumber: "997",
    travelAdvisory: "caution",
    advisoryText: "Generally safe. Houthi drone/missile attacks have targeted UAE infrastructure in the past. Strict local laws apply.",
    embassies: [
      { country: "🇺🇸 US", phone: "+971-2-414-2200", address: "Embassies District, Abu Dhabi" },
      { country: "🇬🇧 UK", phone: "+971-2-610-1100", address: "Al Seef St, Abu Dhabi" },
      { country: "🇩🇪 Germany", phone: "+971-2-644-6693", address: "Al Khalidiyah, Abu Dhabi" },
    ],
    tips: [
      "Previous Houthi drone attacks on Abu Dhabi — stay aware of air defense alerts",
      "Strict laws on social media posts — do not criticize government or religion",
      "Alcohol permitted in licensed venues only — public intoxication is illegal",
      "Carry ID at all times",
      "Respect Ramadan fasting rules — no eating/drinking in public during daylight",
    ],
  },
  bahrain: {
    country: "Bahrain",
    emergencyNumber: "999",
    policeNumber: "999",
    ambulanceNumber: "999",
    fireNumber: "999",
    travelAdvisory: "caution",
    advisoryText: "Generally safe but proximity to Iran raises regional threat level. Occasional protests in Shia-majority areas.",
    embassies: [
      { country: "🇺🇸 US", phone: "+973-1724-2700", address: "Zinj, Manama" },
      { country: "🇬🇧 UK", phone: "+973-1757-4100", address: "Manama" },
    ],
    tips: [
      "US 5th Fleet headquartered here — potential military target",
      "Avoid protests and political gatherings",
      "Respect local customs and dress modestly",
      "Alcohol available in licensed hotels",
      "Monitor regional tensions with Iran closely",
    ],
  },
  saudi_arabia: {
    country: "Saudi Arabia",
    emergencyNumber: "911",
    policeNumber: "999",
    ambulanceNumber: "997",
    fireNumber: "998",
    travelAdvisory: "caution",
    advisoryText: "Generally safe in major cities. Houthi missile/drone attacks on southern regions and critical infrastructure remain a threat.",
    embassies: [
      { country: "🇺🇸 US", phone: "+966-11-488-3800", address: "Diplomatic Quarter, Riyadh" },
      { country: "🇬🇧 UK", phone: "+966-11-481-9100", address: "Diplomatic Quarter, Riyadh" },
      { country: "🇩🇪 Germany", phone: "+966-11-277-3600", address: "Diplomatic Quarter, Riyadh" },
    ],
    tips: [
      "Houthi missile/drone attacks possible in southern regions near Yemen border",
      "Aramco facilities have been targeted — avoid industrial zones",
      "Strict dress code — women should wear abaya in public",
      "Photography restrictions near military and government buildings",
      "Carry Iqama/visa at all times — random checks common",
    ],
  },
  kuwait: {
    country: "Kuwait",
    emergencyNumber: "112",
    policeNumber: "112",
    ambulanceNumber: "112",
    fireNumber: "112",
    travelAdvisory: "caution",
    advisoryText: "Generally safe. Proximity to Iraq and Iran creates regional risk. Strict local laws apply.",
    embassies: [
      { country: "🇺🇸 US", phone: "+965-2259-1001", address: "Bayan, Kuwait City" },
      { country: "🇬🇧 UK", phone: "+965-2259-4320", address: "Arabian Gulf St, Kuwait City" },
    ],
    tips: [
      "Close proximity to Iraq — monitor border tensions",
      "Extreme summer heat (50°C+) — stay hydrated",
      "Alcohol is completely illegal in Kuwait",
      "Respect local customs during Ramadan",
      "UXO risk in some desert areas from 1991 Gulf War",
    ],
  },
  jordan: {
    country: "Jordan",
    emergencyNumber: "911",
    policeNumber: "911",
    ambulanceNumber: "911",
    fireNumber: "911",
    travelAdvisory: "caution",
    advisoryText: "Generally stable but borders with Syria and Iraq create spillover risk. Protests possible in Amman.",
    embassies: [
      { country: "🇺🇸 US", phone: "+962-6-590-6000", address: "Al-Umawyeen St, Amman" },
      { country: "🇬🇧 UK", phone: "+962-6-590-9200", address: "Abdoun, Amman" },
      { country: "🇩🇪 Germany", phone: "+962-6-590-1170", address: "Benghazi St, Amman" },
    ],
    tips: [
      "Avoid Syrian and Iraqi border regions",
      "Jordan has intercepted missiles/drones transiting its airspace",
      "Protests near mosques on Fridays — avoid Al-Husseini area",
      "Petra and tourist areas are generally very safe",
      "Keep passport copy separate from original",
    ],
  },
};

const advisoryColors: Record<string, { color: string; label: string }> = {
  safe: { color: "hsl(var(--success))", label: "SAFE" },
  caution: { color: "hsl(var(--warning))", label: "EXERCISE CAUTION" },
  avoid: { color: "hsl(var(--danger))", label: "AVOID TRAVEL" },
  do_not_travel: { color: "hsl(var(--destructive))", label: "DO NOT TRAVEL" },
};

const CountryEmergencyGuide = ({ embedded }: { embedded?: boolean }) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { isPremium } = useAuth();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const countries = Object.entries(GUIDES).filter(([key, guide]) =>
    !search || guide.country.toLowerCase().includes(search.toLowerCase())
  );

  const guide = selected ? GUIDES[selected] : null;
  const advisory = guide ? advisoryColors[guide.travelAdvisory] : null;

  if (!isPremium && !embedded) {
    return (
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-warning/5 to-transparent" />
            <div className="relative">
              <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="mb-2 text-2xl font-bold text-foreground">📋 Country Emergency Guide</h2>
              <p className="mb-6 text-muted-foreground max-w-lg mx-auto">
                Emergency numbers, embassy contacts, survival tips, and travel advisories for conflict zones.
              </p>
              <Button
                onClick={() => setPricingOpen(true)}
                className="bg-primary text-primary-foreground"
              >
                <Zap className="mr-2 h-4 w-4" /> Unlock Emergency Guides
              </Button>
            </div>
          </div>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </section>
    );
  }

  const Wrapper = embedded ? "div" : "section";
  const wrapperProps = embedded ? {} : { id: "emergency-guide", className: "border-b border-border" };

  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
        {!embedded && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-warning" /> Country Emergency Guide
            </h2>
            <p className="text-sm text-muted-foreground">
              Emergency contacts, embassy info, and survival tips for conflict zones
            </p>
          </div>
        )}

        <div className="mb-6">
          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country..."
              className="bg-background pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {countries.map(([key, guide]) => (
              <Button
                key={key}
                variant={selected === key ? "default" : "outline"}
                size="sm"
                onClick={() => setSelected(key)}
                className={selected === key ? "bg-primary text-primary-foreground" : ""}
              >
                {guide.country}
              </Button>
            ))}
          </div>
        </div>

        {guide && advisory && (
          <div className="space-y-4">
            <div
              className="rounded-lg border p-4 flex items-center gap-3"
              style={{ borderColor: advisory.color, background: `${advisory.color}10` }}
            >
              <AlertTriangle className="h-6 w-6 flex-shrink-0" style={{ color: advisory.color }} />
              <div>
                <Badge className="mb-1 font-mono text-xs" style={{ background: advisory.color, color: "hsl(var(--background))" }}>
                  {advisory.label}
                </Badge>
                <p className="text-sm text-foreground">{guide.advisoryText}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
                  <Phone className="h-4 w-4 text-destructive" /> Emergency Numbers
                </h3>
                <div className="space-y-2">
                  {[
                    ["General Emergency", guide.emergencyNumber],
                    ["Police", guide.policeNumber],
                    ["Ambulance", guide.ambulanceNumber],
                    ["Fire", guide.fireNumber],
                  ].map(([label, num]) => (
                    <div key={label} className="flex justify-between rounded bg-background px-3 py-2">
                      <span className="text-sm text-muted-foreground">{label}</span>
                      <span className="font-mono font-bold text-foreground">{num}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
                  <Building2 className="h-4 w-4 text-primary" /> Embassies
                </h3>
                {guide.embassies.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    Most foreign embassies have suspended operations in this country.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {guide.embassies.map((emb) => (
                      <div key={emb.country} className="rounded bg-background p-3">
                        <p className="text-sm font-semibold text-foreground">{emb.country}</p>
                        <p className="font-mono text-xs text-primary">{emb.phone}</p>
                        <p className="text-xs text-muted-foreground">{emb.address}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
                <Shield className="h-4 w-4 text-success" /> Survival Tips
              </h3>
              <div className="space-y-2">
                {guide.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-0.5 text-primary">▸</span>
                    {tip}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!selected && (
          <div className="rounded-lg border border-dashed border-border bg-card/50 p-12 text-center">
            <Globe className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">Select a country above to view its emergency guide</p>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default CountryEmergencyGuide;
